import httpClient from "@plugins/http";
import { isEmpty } from "@utils/var.check";
import { ApiResponse } from "@apis/api.ts";
import { GameAwardsData } from "@modules/editor/types.ts";
import { GameAwardPreview } from "@/modules/attempt/types";

export default {
  /**
   * Fetch Gamification data
   * @params {*} Course Uuid, ResourceStatus
   */
  fetchGameBook(uuid, resourceStatus) {
    return httpClient.get(`/apis/game-book/get/${uuid}?status=${resourceStatus}`);
  },
  /**
   * Create Game Book with default values
   */
  createDefaultGameBook(uuid, resourceStatus) {
    return httpClient.post(`/apis/game-book/create/${uuid}?status=${resourceStatus}`);
  },
  /**
   * Update Game Book
   */
  updateGameBook(params) {
    return httpClient.post(`/apis/game-book/save`, params, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  /**
   * import Game Book
   */
  importGameBook({ sourceGameBookId, targetGameBookId }) {
    const formData = new FormData();
    formData.append("sourceGameBookId", sourceGameBookId);
    formData.append("targetGameBookId", targetGameBookId);
    return httpClient.post(`/apis/game-book/import`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  /**
   * Fetch Game User Info
   */
  fetchGameUserInfo(params) {
    return httpClient.get(`/apis/game-user-info/get`, { params: params });
  },
  fetchGameAwards(requestData) {
    const timeStamp = Date.now();
    return httpClient.get(`/apis/game-award/get`, {
      params: {
        ...requestData,
        t: timeStamp,
      },
    });
  },
  fetchUserGameAwardHistory(requestData) {
    return httpClient.get(`/apis/game-award-history/get`, {
      params: {
        userId: requestData.userId,
        gameBookId: requestData.gameBookId,
        type: requestData.type,
        attemptableUuid: requestData.attemptableUuid,
        attemptableType: requestData.attemptableType,
      },
    });
  },
  reorderGameAward(requestData) {
    const formData = new FormData();
    Object.keys(requestData).forEach((key) => {
      formData.append(key, requestData[key]);
    });
    return httpClient.post(`/apis/game-award/reorder`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  updateGameStory(data) {
    return httpClient.post(`/apis/game-award/update`, data, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
  duplicateGameAwardWithinSameGameBook(sourceGameAwardId) {
    const formData = new FormData();
    formData.set("sourceGameAwardId", sourceGameAwardId);
    return httpClient.post(`/apis/game-award/same-game-book-duplicate`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  deleteGameAward(requestData) {
    const formData = new FormData();
    Object.keys(requestData).forEach((key) => {
      formData.append(key, requestData[key]);
    });
    return httpClient.post(`/apis/game-award/delete`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  fetchGameConditions(params) {
    return httpClient.get(`/apis/game-condition/get`, {
      params: params,
    });
  },
  /**
   * Save Game Condition
   */
  saveGameCondition(requestData) {
    return httpClient.post(`/apis/game-condition/save`, requestData);
  },
  fetchIndividualLeaderboard(params) {
    return httpClient.get(`/apis/gamification/leaderboard`, {
      params: params,
    });
  },
  fetchTeamLeaderboard(params) {
    return httpClient.get(`/apis/gamification/game-team/leaderboard`, {
      params: params,
    });
  },
  fetchTeamAchievements(params) {
    return httpClient.get(`/apis/gamification/game-team/achievements`, {
      params: params,
    });
  },
  fetchStudentGameTeam(params) {
    return httpClient.get(`/apis/gamification/game-team/get`, {
      params: params,
    });
  },
  fetchGameTeamMembers(params) {
    return httpClient.get(`/apis/gamification/game-team/get-details`, {
      params: params,
    });
  },
  updateLeaderboard(requestData) {
    const formData = new FormData();
    Object.keys(requestData).forEach((key) => {
      formData.append(key, requestData[key]);
    });
    return httpClient.post(`/apis/gamification/leaderboard/update`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  updateTeamLeaderboard(requestData) {
    const formData = new FormData();
    Object.keys(requestData).forEach((key) => {
      formData.append(key, requestData[key]);
    });
    return httpClient.post(`/apis/gamification/team-leaderboard/update`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  /**
   * Fetch User Game Progress
   */
  fetchGameProgress(uuid, requestData) {
    return httpClient.get(`/apis/gamification/progress/get/${uuid}`, {
      params: requestData,
    });
  },
  /**
   * Fetch User Unseen Game Award History
   */
  async fetchUserUnseenGameAwardHistory(
    lessonUuid,
    requestData,
  ): Promise<ApiResponse<GameAwardPreview[]>> {
    return await httpClient.get(`/apis/gamification/unseen/award-history/get/${lessonUuid}`, {
      params: requestData,
    });
  },
  /**
   * Read game award and fetch latest User Unseen Game Award History
   */
  readGameAward(requestData) {
    const params = new URLSearchParams();
    params.append("gameAwardId", requestData.gameAwardId);
    params.append("attemptableUuid", requestData.attemptableUuid);
    params.append("attemptableType", requestData.attemptableType);
    return httpClient.post(`/apis/gamification/game-award/read`, params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  /**
   * Update experience point
   */
  updateExperiencePoint(requestData) {
    return httpClient.post(`/apis/gamification/experience/update`, requestData);
  },
  /**
   * Award game story/achievement
   */
  updateGameAward(requestData) {
    return httpClient.post(`/apis/gamification/award/update`, requestData);
  },
  /**
   * Fetch achievements summary of a single user (lesson or assignment attempt)
   */
  fetchUserAchievementSummary(params) {
    return httpClient.get(`/apis/game-user-achievement-summary/get`, { params: params });
  },
  /**
   * Fetch achievements summary of multiple users in an assignment
   */
  fetchAchievementSummary(params) {
    return httpClient.get(`/apis/assignment-game-achievement-summary/get`, { params: params });
  },
  /**
   * Fetch Game Experience Viewing Dropdown List
   */
  fetchRankingConditionActivityQuizList(lessonUuid, status) {
    return httpClient.get(
      `/apis/gamification/quiz/ranking-condition/list/${lessonUuid}?status=${status}`,
    );
  },
  /**
   * Fetch Quiz Ranking Leaderboard
   */
  fetchIndividualQuizRankingLeaderboard(params) {
    return httpClient.get(`/apis/gamification/quiz/leaderboard`, {
      params: params,
    });
  },
  /**
   * Fetch User's list of quiz ranking achievements summary
   */
  fetchQuizRankingAchievementSummary(params) {
    return httpClient.get(`/apis/gamification/quiz/ranking-achievement-summary/get`, {
      params: params,
    });
  },
  /**
   * Check if quiz ranking is awarded by teacher
   */
  fetchQuizRankingAwarded(lessonUuid, params) {
    return httpClient.get(`/apis/gamification/quiz/is-ranking-awarded/${lessonUuid}`, {
      params: params,
    });
  },

  /**
   * Fetch user's list of game avatar with award information
   */
  fetchUserGameAvatars(requestData) {
    const params = {
      params: {
        userId: requestData.userId,
        gameBookId: requestData.gameBookId,
        attemptableUuid: requestData.attemptableUuid,
        attemptableType: requestData.attemptableType,
      },
    };
    return httpClient.get(`/apis/gamification/game-avatar/get`, params);
  },

  /**
   * update game avatar selected by user
   */
  selectGameAvatar(requestData) {
    const params = new URLSearchParams();
    params.append("gameBookId", requestData.gameBookId);
    if (!isEmpty(requestData.gameAwardId)) {
      params.append("gameAwardId", requestData.gameAwardId);
    }
    params.append("attemptableUuid", requestData.attemptableUuid);
    params.append("attemptableType", requestData.attemptableType);
    return httpClient.post(`/apis/gamification/game-avatar/select`, params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  /**
   * Confirm ranking for quiz and release game awards to students
   */
  confirmRankingForQuizRankGameCondition(params) {
    const urlParams = new URLSearchParams();
    urlParams.append("assignmentUuid", params.assignmentUuid);
    urlParams.append("quizId", params.quizId);
    return httpClient.post(`/apis/gamification/quiz/leaderboard/confirm?${urlParams}`);
  },
  /**
   * Update game choice history
   */
  updateGameChoiceHistory(requestData) {
    const params = new URLSearchParams();
    params.append("gameAwardId", requestData.gameAwardId);
    if (requestData?.gameChoiceId ?? false) {
      params.append("gameChoiceId", requestData.gameChoiceId);
    }
    params.append("answerText", requestData?.answerText ?? "");
    params.append("choiceType", requestData.choiceType);
    params.append("attemptableUuid", requestData.attemptableUuid);
    params.append("attemptableType", requestData.attemptableType);
    return httpClient.post(`/apis/gamification/game-choice-history/update`, params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  /**
   * Fetch game choice history
   */
  fetchGameChoiceHistory(requestData) {
    const params = {
      params: {
        gameAwardId: requestData.gameAwardId,
        userId: requestData.userId,
        attemptableUuid: requestData.attemptableUuid,
        attemptableType: requestData.attemptableType,
      },
    };
    return httpClient.get(`/apis/gamification/game-choice-history/get`, params);
  },

  async createGameAward(params: GameAwardsData): Promise<ApiResponse<void>> {
    return await httpClient.post(`/apis/authoring-copilot/gamification/create`, params, {
      headers: {
        "X-With-Richtext": true,
      },
    });
  },
};
